"use client"

import { useState, useEffect } from "react"
import { ChevronRight, ChevronLeft, Award, RotateCcw } from "lucide-react"

interface QuizQuestion {
  id: string
  question: string
  options: string[]
  correctAnswer: number
}

interface QuizComponentProps {
  questions: QuizQuestion[]
  onQuizComplete: (answers: { questionId: string; answer: number }[]) => void
  getNewQuestions: () => QuizQuestion[]
}

export function QuizComponent({ questions, onQuizComplete, getNewQuestions }: QuizComponentProps) {
  const [currentQuestions, setCurrentQuestions] = useState<QuizQuestion[]>(questions)
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
  const [answers, setAnswers] = useState<number[]>(Array(questions.length).fill(-1))
  const [quizCompleted, setQuizCompleted] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const currentQuestion = currentQuestions[currentQuestionIndex]
  const hasAnswered = answers[currentQuestionIndex] !== -1
  const isLastQuestion = currentQuestionIndex === currentQuestions.length - 1

  const handleAnswerSelect = (optionIndex: number) => {
    const newAnswers = [...answers]
    newAnswers[currentQuestionIndex] = optionIndex
    setAnswers(newAnswers)
  }

  const handleNext = () => {
    if (currentQuestionIndex < currentQuestions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1)
    } else {
      handleSubmitQuiz()
    }
  }

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1)
    }
  }

  const handleSubmitQuiz = async () => {
    setIsSubmitting(true)
    
    // Prepare answers for submission
    const submittedAnswers = currentQuestions.map((question, index) => ({
      questionId: question.id,
      answer: answers[index]
    }))
    
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 1000))
    
    // Call the completion handler
    onQuizComplete(submittedAnswers)
    setQuizCompleted(true)
    setIsSubmitting(false)
  }

  const handleRetryQuiz = () => {
    // Get new set of questions
    const newQuestions = getNewQuestions()
    setCurrentQuestions(newQuestions)
    setAnswers(Array(newQuestions.length).fill(-1))
    setCurrentQuestionIndex(0)
    setQuizCompleted(false)
    setIsSubmitting(false)
  }

  // Reset when initial questions change
  useEffect(() => {
    setCurrentQuestions(questions)
    setAnswers(Array(questions.length).fill(-1))
    setCurrentQuestionIndex(0)
    setQuizCompleted(false)
  }, [questions])

  if (quizCompleted) {
    return (
      <div className="bg-slate-100 rounded-lg border border-slate-200 p-8 text-center">
        <div className="flex justify-center mb-4">
          <Award className="w-16 h-16 text-green-500" />
        </div>
        <h3 className="text-2xl font-bold text-slate-900 mb-4">Quiz Submitted Successfully! 🎉</h3>
        <p className="text-slate-600 mb-2">
          Thank you for completing the quiz!
        </p>
        <p className="text-slate-600 mb-6">
          Your answers have been recorded and submitted successfully.
        </p>
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <p className="text-green-700 font-medium">
            Your responses have been saved and submitted.
          </p>
        </div>
        <button
          onClick={handleRetryQuiz}
          className="flex items-center justify-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium min-h-[44px] mx-auto"
        >
          <RotateCcw className="w-4 h-4" />
          Take Another Quiz
        </button>
      </div>
    )
  }

  return (
    <div className="bg-slate-100 rounded-lg border border-slate-200 p-6">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-bold text-slate-900">Lesson Quiz</h3>
        <span className="text-sm text-slate-500">
          Question {currentQuestionIndex + 1} of {currentQuestions.length}
        </span>
      </div>

      {/* Progress Bar */}
      <div className="w-full bg-slate-200 rounded-full h-2 mb-6">
        <div
          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
          style={{ width: `${((currentQuestionIndex + 1) / currentQuestions.length) * 100}%` }}
        />
      </div>

      {/* Question */}
      <div className="mb-6">
        <p className="text-lg font-medium text-slate-900 mb-4">
          {currentQuestion.question}
        </p>
        
        {/* Options */}
        <div className="space-y-3">
          {currentQuestion.options.map((option, index) => (
            <button
              key={index}
              onClick={() => handleAnswerSelect(index)}
              disabled={isSubmitting}
              className={`w-full text-left p-4 rounded-lg border transition-all ${
                answers[currentQuestionIndex] === index
                  ? "border-blue-500 bg-blue-50 text-blue-700"
                  : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
              } ${isSubmitting ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
            >
              {option}
            </button>
          ))}
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between items-center">
        <button
          onClick={handlePrevious}
          disabled={currentQuestionIndex === 0 || isSubmitting}
          className="flex items-center gap-2 px-4 py-2 text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors min-h-[44px]"
        >
          <ChevronLeft className="w-4 h-4" />
          Previous
        </button>

        <button
          onClick={handleNext}
          disabled={!hasAnswered || isSubmitting}
          className="flex items-center gap-2 px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors min-h-[44px]"
        >
          {isSubmitting ? (
            "Submitting..."
          ) : isLastQuestion ? (
            "Submit Quiz"
          ) : (
            "Next Question"
          )}
          {!isSubmitting && <ChevronRight className="w-4 h-4" />}
        </button>
      </div>

      {isSubmitting && (
        <div className="mt-4 text-center">
          <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          <p className="text-slate-600 mt-2">Submitting your answers...</p>
        </div>
      )}
    </div>
  )
}